import { Box, TextField, useTheme } from "@mui/material";
import { disabledBlockSx } from "../disabledStyles";
import { XImageRender } from "@platformx/x-image-render";
import { AutoCompleteDropDown } from "../AutoCompleteDropDown/AutoCompleteDropDown";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";

const MediaHandle = ({
  field,
  state,
  setState,
  errors,
  register,
  clearErrors,
  isDisabled = false,
}: any) => {
  const theme = useTheme();
  const selectedHandles = (state?.[field?.name] || []).map((item: any) => ({
    ...item,
    media_name: item.media_name || item.label || item.value,
  }));
  const options = (field?.options || []).map((option: any) => ({
    ...option,
    media_name: option.label,
  }));

  const handleSelectionChange = (newValue: any[]) => {
    clearErrors(field?.name);
    const merged = (newValue || []).map(
      (option) =>
        selectedHandles.find((item: any) => item.media_name === option.media_name) || {
          media_name: option.media_name,
          media_url: "",
          icon_image: "",
          original_image: null,
          published_images: [],
        },
    );
    setState({ ...state, [field?.name]: merged });
  };

  const updateHandleImage = (index: number, imageData: any) => {
    const updated = [...selectedHandles];
    const originalImage = imageData?.original_image;
    const iconImage =
      originalImage?.Thumbnail ||
      (originalImage?.original_image_relative_path
        ? `${originalImage.original_image_relative_path}.${originalImage.ext}`
        : "");
    updated[index] = {
      ...updated[index],
      icon_image: iconImage || updated[index]?.icon_image || "",
      original_image: originalImage || null,
      published_images: imageData?.published_images || [],
    };
    clearErrors(`${field?.name}[${index}].icon_image`);
    setState({ ...state, [field?.name]: updated });
  };

  const updateHandleUrl = (index: number, url: string) => {
    const updated = [...selectedHandles];
    updated[index] = { ...updated[index], media_url: url };
    setState({ ...state, [field?.name]: updated });
  };

  return (
    <Box id={`${field?.name}_id`} sx={disabledBlockSx(isDisabled)}>
      <AutoCompleteDropDown
        options={options}
        state={{ ...state, [field?.name]: selectedHandles }}
        field={field}
        optionFormat='media_name'
        error={errors?.[field?.name]}
        register={register}
        handleChangeOn={(newValue: any) => handleSelectionChange(newValue)}
        isDisabled={isDisabled}
      />
      {selectedHandles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {selectedHandles.map((handle: any, index: number) => {
            const imageError = errors?.[field?.name]?.[index]?.icon_image;
            const urlError = errors?.[field?.name]?.[index]?.media_url;
            const missingParts = [...(imageError ? ["image"] : []), ...(urlError ? ["url"] : [])];
            const rowError = missingParts.length
              ? { message: `${handle?.media_name} ${missingParts.join(" and ")} is required` }
              : undefined;
            return (
              <Box
                key={handle?.media_name}
                id={`${field?.name}[${index}]_id`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mt: index > 0 ? 2.5 : 0,
                }}>
                <Box
                  sx={{
                    height: "48px",
                    width: "48px",
                    flexShrink: 0,
                    borderRadius: "5px",
                    overflow: "hidden",
                    border: imageError ? `solid 1px ${theme.palette.customColor.ERROR}` : "none",
                  }}>
                  <XImageRender
                    callBack={(data: any) => updateHandleImage(index, data)}
                    editData={{
                      original_image: handle?.original_image,
                      published_images: handle?.published_images,
                    }}
                    isCrop={false}
                    containerSize={{ width: "48px", height: "48px" }}
                  />
                </Box>
                <HiddenTextBox
                  error={!!imageError}
                  value={handle?.icon_image || ""}
                  register={register}
                  name={`${field?.name}[${index}].icon_image`}
                  required={!handle?.icon_image ? `${handle?.media_name} image is required` : ""}
                />
                <Box sx={{ ml: "11px", flexGrow: 1 }}>
                  <TextField
                    variant='outlined'
                    size='small'
                    fullWidth
                    placeholder={handle?.media_name}
                    value={handle?.media_url || ""}
                    error={!!urlError}
                    {...register(`${field?.name}[${index}].media_url`, {
                      required: {
                        value: true,
                        message: `${handle?.media_name} URL is required`,
                      },
                      onChange: (event: any) => {
                        if (urlError) {
                          clearErrors(`${field?.name}[${index}].media_url`);
                        }
                        updateHandleUrl(index, event.target.value);
                      },
                    })}
                  />
                  <ErrorMessageHandle error={rowError} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MediaHandle;
